import { useState } from 'react'
import { Search, Filter } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Avatar } from '@/components/common/Avatar'
import { Spinner } from '@/components/common/Spinner'
import { formatConversationTime, formatInternationalLeadNumber } from '@/utils/formatters'
import type { Conversation } from '@/types'

interface ConversationListProps {
  conversations: Conversation[]
  activeConversation?: Conversation | null
  isLoading?: boolean
  onSelect: (conversation: Conversation) => void
  onOpenContactDetails?: (conversation: Conversation) => void
  onSearch?: (query: string) => void
  onFilterStatus?: (status: string) => void
  instanceLabelById?: Map<string, string>
}

const INSTANCE_TAG_PALETTE = [
  { bg: 'rgba(99, 102, 241, 0.16)', border: 'rgba(99, 102, 241, 0.32)', text: '#6366F1' },
  { bg: 'rgba(16, 185, 129, 0.16)', border: 'rgba(16, 185, 129, 0.32)', text: '#10B981' },
  { bg: 'rgba(245, 158, 11, 0.16)', border: 'rgba(245, 158, 11, 0.36)', text: '#D97706' },
  { bg: 'rgba(236, 72, 153, 0.16)', border: 'rgba(236, 72, 153, 0.32)', text: '#EC4899' },
  { bg: 'rgba(59, 130, 246, 0.16)', border: 'rgba(59, 130, 246, 0.32)', text: '#3B82F6' },
  { bg: 'rgba(168, 85, 247, 0.16)', border: 'rgba(168, 85, 247, 0.32)', text: '#A855F7' },
  { bg: 'rgba(20, 184, 166, 0.16)', border: 'rgba(20, 184, 166, 0.32)', text: '#14B8A6' },
  { bg: 'rgba(239, 68, 68, 0.16)', border: 'rgba(239, 68, 68, 0.32)', text: '#EF4444' },
] as const

function pickInstanceTagStyle(instanceId: string): typeof INSTANCE_TAG_PALETTE[number] {
  let hash = 0
  for (let i = 0; i < instanceId.length; i++) {
    hash = (hash * 31 + instanceId.charCodeAt(i)) | 0
  }
  const idx = Math.abs(hash) % INSTANCE_TAG_PALETTE.length
  return INSTANCE_TAG_PALETTE[idx]
}

const statusFilters = [
  { value: 'all', label: 'Todas' },
  { value: 'open', label: 'Abertas' },
  { value: 'waiting', label: 'Aguardando' },
  { value: 'closed', label: 'Fechadas' },
]

const priorityColors = {
  urgent: 'border-l-[var(--color-error)]',
  high: 'border-l-[var(--color-warning)]',
  normal: 'border-l-transparent',
  low: 'border-l-[var(--text-muted)]',
}

export function ConversationList({
  conversations,
  activeConversation,
  isLoading = false,
  onSelect,
  onOpenContactDetails,
  onSearch,
  onFilterStatus,
  instanceLabelById,
}: ConversationListProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilter, setActiveFilter] = useState('all')

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    onSearch?.(value)
  }

  const handleFilterChange = (status: string) => {
    setActiveFilter(status)
    onFilterStatus?.(status === 'all' ? '' : status)
  }

  const filteredConversations = conversations.filter((conv) => {
    const matchesSearch =
      !searchQuery ||
      conv.contact?.name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      conv.contact?.phone_number?.includes(searchQuery)

    const matchesFilter =
      activeFilter === 'all' || conv.status === activeFilter

    return matchesSearch && matchesFilter
  })

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-[var(--border-subtle)]">
        <div className="relative flex items-center">
          <Search
            size={16}
            className="absolute left-3 text-[var(--text-muted)] pointer-events-none"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Buscar conversa..."
            className={cn(
              'w-full pl-9 pr-4 py-2 rounded-[var(--radius-md)]',
              'text-sm glass-input',
              'focus:outline-none',
            )}
          />
        </div>
      </div>

      {/* Status filters */}
      <div className="flex gap-1 px-3 py-2 border-b border-[var(--border-subtle)] overflow-x-auto scrollbar-hide shrink-0">
        {statusFilters.map((filter) => (
          <button
            key={filter.value}
            onClick={() => handleFilterChange(filter.value)}
            className={cn(
              'px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-150',
              activeFilter === filter.value
                ? 'bg-[var(--color-brand)] text-white'
                : 'bg-[var(--glass-medium)] text-[var(--text-muted)] hover:text-[var(--text-primary)]',
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Conversations */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" label="Carregando conversas..." />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <p className="text-[var(--text-muted)] text-sm">
              {searchQuery
                ? 'Nenhuma conversa encontrada'
                : 'Sem conversas ainda'}
            </p>
          </div>
        ) : (
          filteredConversations.map((conv) => (
            <ConversationItem
              key={conv.id}
              conversation={conv}
              isActive={activeConversation?.id === conv.id}
              onClick={() => onSelect(conv)}
              onOpenContactDetails={
                onOpenContactDetails ? () => onOpenContactDetails(conv) : undefined
              }
              instanceLabel={instanceLabelById?.get(conv.instance_id)}
            />
          ))
        )}
      </div>
    </div>
  )
}

interface ConversationItemProps {
  conversation: Conversation
  isActive: boolean
  onClick: () => void
  onOpenContactDetails?: () => void
  instanceLabel?: string
}

function ConversationItem({
  conversation,
  isActive,
  onClick,
  onOpenContactDetails,
  instanceLabel,
}: ConversationItemProps) {
  const contact = conversation.contact
  const name =
    contact?.name ||
    formatInternationalLeadNumber(contact?.phone_number ?? contact?.jid ?? '') ||
    'Desconhecido'

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
        }
      }}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-3',
        'border-l-2 transition-all duration-150 text-left',
        'hover:bg-[var(--glass-light)]',
        priorityColors[conversation.priority],
        isActive
          ? 'bg-[var(--color-brand-light)] border-l-[var(--color-brand)]'
          : '',
      )}
    >
      <Avatar name={name} src={contact?.avatar_url} size="md" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onOpenContactDetails?.()
            }}
            className={cn(
              'truncate text-left text-sm font-medium transition hover:text-[var(--color-brand)]',
              isActive
                ? 'text-[var(--color-brand)]'
                : 'text-[var(--text-primary)]',
              conversation.unread_count > 0 && 'font-semibold',
            )}
          >
            {name}
          </button>
          <span className="text-[10px] text-[var(--text-muted)] shrink-0">
            {formatConversationTime(conversation.last_message_at)}
          </span>
        </div>
        {instanceLabel && (
          <div className="mb-0.5 flex">
            <span
              className="inline-flex max-w-full truncate rounded-full border px-2 py-px text-[10px] font-semibold uppercase tracking-wide"
              style={{
                backgroundColor: pickInstanceTagStyle(conversation.instance_id).bg,
                borderColor: pickInstanceTagStyle(conversation.instance_id).border,
                color: pickInstanceTagStyle(conversation.instance_id).text,
              }}
            >
              {instanceLabel}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 min-w-0">
          {conversation.last_message_preview ? (
            <span className="text-xs text-[var(--text-muted)] truncate min-w-0 flex-1">
              {conversation.last_message_direction === 'outbound' && (
                <span className="text-[var(--color-brand)] mr-1">Você:</span>
              )}
              {conversation.last_message_preview}
            </span>
          ) : (
            <span className="text-xs text-[var(--text-muted)] truncate">
              {conversation.status === 'open'
                ? 'Aberta'
                : conversation.status === 'waiting'
                ? 'Aguardando'
                : 'Fechada'}
            </span>
          )}

          {conversation.unread_count > 0 && (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--color-brand)] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
              {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
